/**
 * Стартовая последовательность приложения — экран SYS-17 (CC §4.3).
 *
 *  1. `GET /config` без сессии: лимиты, min_client_versions, реакции,
 *     категории жалоб, legal, captcha, push.
 *  2. Нет сессии → A-01/A-02; цель deep link сохраняется до входа.
 *  3. `GET /me`: `onboarding` → O-07; непустой `consents_required` → SYS-05; 401 → SYS-07.
 *  4–7. Snapshot, перезагрузка списков, cursor, WebSocket — внутри SyncEngine.
 *  8. `POST /push/subscriptions` — только при уже выданном разрешении
 *     (§8.2 п. 3–4): системный запрос идёт лишь из N-03 по жесту пользователя.
 */
import { api, setApiLocale, setApiSessionOwner, unwrap } from '@shared/api/client'
import type { Me } from '@shared/api/types'
import { ApiError, isNetworkError } from '@shared/api/errors'
import {
  applyStorageBudgetAtStart,
  getActiveUserId,
  getOrCreateInstallId,
  markWebkitNotifications,
  meRepo,
  mirrorSettingsForWorker,
  openUserDb,
  setActiveUser,
  pendingServerLogout,
  pendingUserDbDeletions,
  userDbName,
} from '@shared/db'
import { countInstallSession } from '@shared/pwa/install'
import { retryPushSubscriptionIfFlagged, syncPushSubscription } from '@shared/push'
import { SyncEngine } from '@shared/sync/engine'
import { publishDeviceKeysQuietly } from '@shared/secret'
import { setSyncEngine, syncEngine } from '@shared/sync/instance'
import { releaseLeadership, startLeaderElection } from '@shared/ws/leader'

import { useAppStore, type BlockingState } from '../model/app-store'
import { isBannedAppealSession } from '../model/account-access'
import { usePresenceStore } from '../model/presence-store'
import { useTypingStore } from '../model/typing-store'
import { applyDocumentLanguage } from '../model/locale'
import { startMeMirror, stopMeMirror } from './me-mirror'
import { compareSemver } from './semver'

// Реэкспорт: экраны импортируют `refreshMe` отсюда исторически и по смыслу.
export { refreshMe } from './refresh-me'
import { setActivationListener } from './activation'
import { attachEngineCallbacks } from './engine-callbacks'
import {
  resumeLocalAuthTransitionRequests,
  type AuthTransitionGuard,
  withAuthTransitionOptions,
} from './auth-transition'
import {
  handleTerminalAccountState,
  handleOwnerChange,
  recoverPendingServerLogout,
  recoverPendingUserDbDeletions,
  resolvePendingDeletionBeforeSession,
} from './session-lifecycle'

let started = false

type TerminalAccountKind = 'account_suspended' | 'account_pending_deletion'

interface ColdBootAccountErrorResult {
  handled: boolean
  terminalAccount?: { userId: string; kind: TerminalAccountKind }
}

/** Терминальный `/me` обязан сам выставить SYS-08/09, не полагаясь на порядок React effects. */
export function handleColdBootAccountError(
  error: unknown,
  previousUserId: string | null,
  options: { publishPhase?: boolean } = {},
): ColdBootAccountErrorResult {
  if (
    !(error instanceof ApiError) ||
    (error.code !== 'account_restricted' &&
      error.code !== 'account_suspended' &&
      error.code !== 'account_pending_deletion')
  ) {
    return { handled: false }
  }

  useAppStore.getState().setBlocking({ kind: error.code })
  if (options.publishPhase ?? true) useAppStore.getState().setPhase('ready')
  // A restricted response may belong to the appeal-only banned session. Keep
  // the durable owner and user replica so a reload cannot erase the data that
  // the restricted shell needs. The current backend allows `/me`, so the
  // normal path identifies this session precisely from its active sanction.
  if (error.code === 'account_restricted') return { handled: true }

  return previousUserId
    ? { handled: true, terminalAccount: { userId: previousUserId, kind: error.code } }
    : { handled: true }
}

export async function bootstrap(): Promise<void> {
  if (started) return
  started = true

  try {
    await runBootstrap()
  } catch (error) {
    /*
     * Непредвиденная ошибка старта обязана быть ВИДНА. Без этого любая
     * мелочь — недоступный в незащищённом контексте API, отказ IndexedDB
     * в приватном режиме — оставляла бы SYS-17 крутиться вечно, и человек
     * видел бы бесконечную загрузку вместо причины (ПС §19).
     */
    console.error('bootstrap failed', error)
    const store = useAppStore.getState()
    store.setBootError('Не удалось запустить приложение. Обновите страницу.')
    store.setPhase('failed')
  }
}

/** Сколько ждём локальную базу устройства, прежде чем объяснить задержку. */
const DEVICE_DB_TIMEOUT_MS = 6000

function isAuthTransitionAcquireTimeout(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === 'TimeoutError' &&
    error.message === 'auth transition lock acquisition timed out'
  )
}

/** `true` — задача успела, `false` — вышло время. Сама задача не отменяется. */
export async function withDeadline(task: Promise<unknown>, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), ms)
  })

  try {
    return await Promise.race([task.then(() => true), deadline])
  } finally {
    clearTimeout(timer)
  }
}

async function runBootstrap(): Promise<void> {
  const store = useAppStore.getState()

  // Признак движка WebKit — ограничивает набор полей showNotification (§8.3).
  const isWebkit = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  /*
   * Первое обращение к базе устройства может не завершиться: если открыта
   * другая вкладка «Движ» со старой версией базы, обновление схемы блокируется
   * и `open()` ждёт освобождения без ограничения времени. Бесконечная загрузка
   * вместо причины запрещена (ПС §19) — ждём ограниченно и объясняем, что делать.
   */
  const deviceReady = await withDeadline(
    (async () => {
      await markWebkitNotifications(isWebkit)
      await getOrCreateInstallId()
    })(),
    DEVICE_DB_TIMEOUT_MS,
  )

  if (!deviceReady) {
    store.setBootError('Другая вкладка «Движ» держит локальные данные. Закройте её и обновите страницу.')
    store.setPhase('failed')
    return
  }

  // Явный logout обязан удалить всю пользовательскую БД. Если прошлую
  // попытку заблокировала замороженная вкладка, не отправляем даже публичный
  // startup traffic до повторной privacy-cleanup: иначе новая сессия могла бы
  // открыться поверх оставшейся реплики прежнего владельца.
  const pendingDeletes = await pendingUserDbDeletions()
  // На обычном запуске delete-intent отсутствует. Не занимаем auth mutex
  // впустую: Safari может ещё держать Web Lock предыдущего документа в bfcache,
  // хотя удалять нечего. Реальный durable intent по-прежнему fail-closed.
  if (pendingDeletes.length) {
    let pendingDeletesSucceeded = false
    let pendingDeletesFinished = false
    try {
      pendingDeletesFinished = await withDeadline(
        withAuthTransitionOptions(
          async (ensureOwned) => {
            pendingDeletesSucceeded = await recoverPendingUserDbDeletions(ensureOwned)
          },
          {
            fenceOtherTabs: false,
            acquireTimeoutMs: DEVICE_DB_TIMEOUT_MS,
            diagnosticName: 'bootstrap-delete-recovery',
          },
        ),
        DEVICE_DB_TIMEOUT_MS,
      )
    } catch (error) {
      if (!isAuthTransitionAcquireTimeout(error)) throw error
    }
    if (!pendingDeletesFinished || !pendingDeletesSucceeded) {
      store.setBootError(
        'Не удалось удалить локальные данные прошлого аккаунта. Закройте другие вкладки «Движ» и обновите страницу.',
      )
      store.setPhase('failed')
      return
    }
  }

  const pendingLogoutUserId = await pendingServerLogout()
  if (pendingLogoutUserId) {
    let serverLogoutRecovered = false
    let recoveryFinished = false
    try {
      recoveryFinished = await withDeadline(
        withAuthTransitionOptions(
          async (ensureOwned) => {
            serverLogoutRecovered = await recoverPendingServerLogout(ensureOwned)
          },
          {
            fenceOtherTabs: true,
            acquireTimeoutMs: DEVICE_DB_TIMEOUT_MS,
            diagnosticName: 'bootstrap-logout-recovery',
          },
        ),
        DEVICE_DB_TIMEOUT_MS,
      )
    } catch (error) {
      if (!isAuthTransitionAcquireTimeout(error)) throw error
    }
    if (!recoveryFinished || !serverLogoutRecovered) {
      store.setBootError('Выход не завершён на сервере. Проверьте соединение и обновите страницу.')
      store.setPhase('failed')
      return
    }
  }

  /* --- Шаг 1: конфигурация ------------------------------------------- */

  let config
  try {
    config = unwrap(await api.GET('/config'))
  } catch (error) {
    store.setBootError(
      isNetworkError(error)
        ? 'Не удалось связаться с сервисом. Проверьте соединение.'
        : 'Сервис временно недоступен.',
    )
    store.setPhase('failed')
    return
  }

  store.setConfig(config)

  // §9.5 п. 6: версия сравнивается semver, без лексикографики.
  if (compareSemver(__APP_VERSION__, config.min_client_versions.web) < 0) {
    store.setBlocking({ kind: 'client_upgrade_required' })
    store.setPhase('ready')
    return
  }

  /* --- Шаг 3: кто мы ---------------------------------------------------- */

  let terminalAccount: { userId: string; kind: TerminalAccountKind } | undefined
  let postTransitionPhase: SessionStartPhase | 'unauthenticated' | 'failed' | null = null
  let postTransitionSession: SessionStartState | null = null
  try {
    await withAuthTransitionOptions(
      async (ensureOwned) => {
        // `GET /me` и установка owner — одна auth epoch. Если получить Me=A
        // до lock, вход B из другой вкладки мог успеть сменить cookie,
        // а stale A затем удалил бы БД B и смешал две реплики.
        const previousUserId = await getActiveUserId()
        await ensureOwned()
        setApiSessionOwner(previousUserId)
        let me
        try {
          await ensureOwned()
          me = unwrap(await api.GET('/me'))
          await ensureOwned()
        } catch (error) {
          await ensureOwned()
          if (error instanceof ApiError) {
            // 401 без сессии — обычный путь первого запуска, а не ошибка.
            if (error.code === 'unauthenticated' || error.code === 'session_expired') {
              setApiSessionOwner(null)
              postTransitionPhase = 'unauthenticated'
              return
            }
            const terminal = handleColdBootAccountError(error, previousUserId, { publishPhase: false })
            if (terminal.handled) {
              terminalAccount = terminal.terminalAccount
              postTransitionPhase = 'ready'
              return
            }
            postTransitionPhase = 'ready'
            return
          }

          store.setBootError('Не удалось загрузить профиль. Повторите попытку.')
          postTransitionPhase = 'failed'
          return
        }

        postTransitionSession = await startSession(me, ensureOwned, { publishInteractiveState: false })
      },
      {
        fenceOtherTabs: false,
        acquireTimeoutMs: DEVICE_DB_TIMEOUT_MS,
        diagnosticName: 'bootstrap-session',
      },
    )
  } catch (error) {
    if (!isAuthTransitionAcquireTimeout(error)) throw error
    store.setBootError('Другая вкладка «Движ» не завершила смену аккаунта. Закройте её и обновите страницу.')
    store.setPhase('failed')
    return
  }
  if (terminalAccount) handleTerminalAccountState(terminalAccount.userId, terminalAccount.kind)
  // A hard navigation can start a second Safari document immediately after
  // the app becomes interactive. Publish the routeable phase only after the
  // origin-wide auth mutex has been released by withAuthTransitionOptions.
  if (postTransitionSession) publishSessionStartState(postTransitionSession)
  else if (postTransitionPhase) store.setPhase(postTransitionPhase)
}

/**
 * Запускает пользовательскую сессию: открывает реплику, поднимает движок
 * синхронизации, выставляет фазу приложения.
 */
export type SessionStartPhase = 'ready' | 'onboarding'

export interface SessionStartState {
  phase: SessionStartPhase
  blocking: BlockingState | null
}

interface StartSessionOptions {
  /** The transition owner may defer all interactive state until after releasing its mutex. */
  publishInteractiveState?: boolean
}

export function publishSessionStartState(state: SessionStartState): void {
  const store = useAppStore.getState()
  store.setBlocking(state.blocking)
  store.setPhase(state.phase)
}

export async function startSession(
  me: Me,
  ensureAuthTransitionOwned: AuthTransitionGuard = async () => undefined,
  options: StartSessionOptions = {},
): Promise<SessionStartState> {
  const store = useAppStore.getState()
  const appealOnly = isBannedAppealSession(me)
  const publishInteractiveState = options.publishInteractiveState ?? true

  /*
   * CC §4.11 п. 2: вошли под ДРУГИМ `Me.user.id` — база прежнего владельца
   * удаляется целиком, без выборочной чистки. Иначе на устройстве осталась бы
   * чужая переписка, а вкладки продолжили бы работать со старой репликой.
   */
  const previousUserId = await getActiveUserId()
  await ensureAuthTransitionOwned()
  if (previousUserId && previousUserId !== me.user.id) {
    await handleOwnerChange(previousUserId, me.user.id, ensureAuthTransitionOwned)
    await ensureAuthTransitionOwned()
  }
  await resolvePendingDeletionBeforeSession(me.user.id, ensureAuthTransitionOwned)
  await ensureAuthTransitionOwned()

  /*
   * Обычные API остаются закрыты после verify, пока локальная epoch B
   * действительно не готова: база открыта, свежий Me записан, durable owner
   * переключён. Иначе callback старого экрана мог попасть под новую cookie,
   * но ещё писать в реплику A или в закрытую базу.
   */
  const db = openUserDb(me.user.id)
  await db.open()
  await ensureAuthTransitionOwned()
  await meRepo.put(me)
  await ensureAuthTransitionOwned()
  await setActiveUser(me.user.id, userDbName(me.user.id))
  await ensureAuthTransitionOwned()

  store.setMe(me)
  setApiSessionOwner(me.user.id)
  setApiLocale(me.settings.locale || 'ru')
  applyDocumentLanguage(me.settings.locale || 'ru')

  await mirrorSettingsForWorker(me.settings.locale || 'ru', me.settings.notif_preview)
  await ensureAuthTransitionOwned()

  if (appealOnly) {
    // The appeal session must never start snapshot, pending mutations, WebSocket,
    // presence or push registration. Stop leftovers from the revoked pre-ban
    // session while retaining the cookie, owner and `/me` needed by the appeal UI.
    stopMeMirror()
    syncEngine()?.stop()
    setSyncEngine(null)
    releaseLeadership()
    store.setOnline(false)
    resumeLocalAuthTransitionRequests()
    const state: SessionStartState = { phase: 'ready', blocking: { kind: 'account_restricted' } }
    if (publishInteractiveState) publishSessionStartState(state)
    return state
  }

  /*
   * Согласия требуются — SYS-05 поверх интерфейса (BS §15.1). Не во время
   * onboarding: у нового аккаунта `consents_required` заполнен всегда, а сами
   * согласия собирает первый шаг A-05; «Документы обновились» поверх него —
   * ложное сообщение. Если документы сменились до завершения, `complete`
   * ответит 409 `consents`, и O-07 вернёт на тот же шаг.
   */
  const blocking: BlockingState | null =
    me.consents_required?.length && me.user.status !== 'onboarding' ? { kind: 'consent_required' } : null

  /*
   * BS §5.7: в `onboarding` доступны только endpoint'ы с пометкой O — snapshot,
   * списки и WebSocket отвечают 403 `onboarding_incomplete`. Поэтому шаги 4–7
   * (движок синхронизации) запускаются, когда аккаунт стал `active`: сразу или
   * после `POST /me/onboarding/complete` через `refreshMe` → `activation`.
   */
  setActivationListener((activated) => void startSync(activated))

  // Только теперь snapshot/list/push получают право на обычный API-трафик.
  resumeLocalAuthTransitionRequests()

  const nextPhase: SessionStartPhase = me.user.status === 'onboarding' ? 'onboarding' : 'ready'
  if (me.user.status === 'onboarding') {
    /*
     * Реплика нужна и до активации: шаг фото хранит обработанные медиа в базе
     * пользователя (`mediaRepo`), а движок откроет ту же базу позже, идемпотентно.
     */
  } else {
    // The caller can keep the boot/auth shell visible until its auth mutex is
    // fully released. startSync still opens the replica and transport, but it
    // does not expose routeable UI in that mode.
    await startSync(me, false)
  }

  /*
   * Шаг 8. Подписка регистрируется заново после ЛЮБОГО входа (§8.2 п. 4,
   * BS §8.5 п. 9) и сверяет VAPID-ключ с конфигурацией (§8.2 п. 3, D-38).
   * Разрешение при этом не запрашивается — без него функция молча выходит.
   */
  // Вход держит auth-transition lock до завершения общей
  // browser PushSubscription: logout другой вкладки не может её снять.
  await syncPushSubscription(store.config)
  await retryPushSubscriptionIfFlagged(store.config)

  // PWA-07 DoD п. 2: предложение установки — не раньше третьего сеанса.
  void countInstallSession(me.user.id)

  /*
   * IOS §1.5 п. 4: бюджеты хранилища по квоте origin и LRU-вычистка истории.
   * Не на критическом пути старта — в простое, когда первый экран уже виден.
   */
  scheduleIdle(() => void applyStorageBudgetAtStart())
  const state: SessionStartState = { phase: nextPhase, blocking }
  if (publishInteractiveState) publishSessionStartState(state)
  return state
}

/**
 * Шаги 4–7 стартовой последовательности: snapshot, списки, cursor, WebSocket.
 * Идемпотентно — повторный вызов для уже запущенного движка ничего не делает.
 */
async function startSync(me: Me, publishReady = true): Promise<void> {
  const store = useAppStore.getState()
  if (syncEngine()) {
    if (publishReady && store.phase === 'onboarding') store.setPhase('ready')
    return
  }

  // Блокировка привязана к владельцу локальной реплики: только её лидер
  // выполняет snapshot/replay, держит WebSocket и обрабатывает pending.
  startLeaderElection(me.user.id)

  /*
   * Свежий `GET /me` передаётся движку как начальное состояние реплики: только он
   * несёт `user.device_id` текущей сессии (кадр hello, BS §9.3), а при обычном
   * запуске snapshot не запрашивается — без этого hello ушёл бы с записью прошлого сеанса.
   */
  const engine = new SyncEngine(attachEngineCallbacks(me.user.id))
  setSyncEngine(engine)
  // Кадры typing — в общий стор: индикатор нужен и в диалоге, и в списке чатов.
  engine.onTypingFrame((frame) => useTypingStore.getState().apply(frame, me.user.id))
  // Кадры presence — тоже в стор: статусы «в сети» живут в «Контактах» и списке чатов.
  engine.onPresenceFrame((frame) => usePresenceStore.getState().apply(frame))
  await engine.start(me.user.id, me)

  // Дальше `Me` живёт от реплики: сюда попадают и `me.updated` с других устройств.
  startMeMirror(me.user.id)

  // Секретные чаты (v1.13): ключи этого устройства публикуются фоном, чтобы с нами можно
  // было начать секретный чат; старт чата перепроверит набор сам.
  scheduleIdle(() => void publishDeviceKeysQuietly(me.user.device_id))

  if (publishReady) store.setPhase('ready')
}

function scheduleIdle(task: () => void): void {
  const idle = (
    globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
  ).requestIdleCallback
  if (idle) idle(task, { timeout: 5000 })
  else setTimeout(task, 2000)
}
